import { useEffect, useCallback, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { 
  debounce, 
  updateUrlWithTableState, 
  getTableStateFromUrl,
  cleanTableParamsFromUrl
} from './tableUrlUtils';

/**
 * Custom hook for synchronizing table state with URL parameters
 * 
 * @param {Object} config - Configuration object
 * @param {boolean} config.enabled - Whether URL sync is enabled
 * @param {string} config.tableId - Unique identifier for this table (for multiple tables on same page)
 * @param {number} config.debounceMs - Debounce delay for URL updates (default: 300ms)
 * @param {Object} config.initialState - Initial state from table props
 * @param {Function} config.onStateChange - Callback when URL state changes
 * 
 * @returns {Object} - URL sync utilities and state
 */
export const useTableUrlSync = ({
  enabled = false,
  tableId = 'table',
  debounceMs = 300,
  initialState = {},
  onStateChange = () => {}
}) => {
  const location = useLocation();
  const navigate = useNavigate();

  // Get initial state from URL on mount
  const urlState = useMemo(() => {
    if (!enabled) return {};
    return getTableStateFromUrl(location, tableId);
  }, [enabled, location, tableId]);

  // Determine if we should use URL state or provided initial state
  const hasUrlState = useMemo(() => {
    if (!enabled) return false;
    const urlParams = new URLSearchParams(location.search);
    const tableParams = tableId === 'table' 
      ? ['sort', 'search', 'page', 'pageSize']
      : ['sort', 'search', 'page', 'pageSize'].map(p => `${tableId}_${p}`);
    
    return tableParams.some(param => urlParams.has(param));
  }, [enabled, location.search, tableId]);

  // Debounced function to update URL
  const debouncedUpdateUrl = useCallback(
    debounce((tableState) => {
      if (enabled) {
        updateUrlWithTableState(navigate, location, tableState, tableId);
      }
    }, debounceMs),
    [enabled, navigate, location, tableId, debounceMs]
  );

  // Function to update URL with current table state
  const updateUrl = useCallback((tableState) => {
    if (!enabled) return;
    debouncedUpdateUrl(tableState);
  }, [enabled, debouncedUpdateUrl]);

  // Function to clear table parameters from URL
  const clearUrlState = useCallback(() => {
    if (!enabled) return;
    cleanTableParamsFromUrl(navigate, location, tableId);
  }, [enabled, navigate, location, tableId]);

  // Effect to notify parent component of URL state changes
  useEffect(() => {
    if (enabled && hasUrlState) {
      onStateChange(urlState);
    }
  }, [enabled, hasUrlState, urlState, onStateChange]);

  // Function to get merged state (URL state takes precedence over initial state)
  const getMergedState = useCallback(() => {
    if (!enabled || !hasUrlState) {
      return initialState;
    }

    return {
      sortModel: urlState.sortModel || initialState.sortModel || [],
      search: urlState.search || initialState.search || '',
      pagination: {
        ...initialState.pagination,
        ...urlState.pagination
      }
    };
  }, [enabled, hasUrlState, urlState, initialState]);

  return {
    // State
    urlState,
    hasUrlState,
    mergedState: getMergedState(),
    
    // Actions
    updateUrl,
    clearUrlState,
    
    // Utilities
    enabled,
    tableId
  };
};

/**
 * Hook for managing table state with URL synchronization
 * This is a higher-level hook that manages both local state and URL sync
 */
export const useTableStateWithUrl = ({
  enabled = false,
  tableId = 'table',
  debounceMs = 300,
  initialSortModel = [],
  initialSearch = '',
  initialPagination = { page: 0, pageSize: 10 },
  initialColumnVisibility = {}
}) => {
  const [localSortModel, setLocalSortModel] = useState(initialSortModel);
  const [localSearch, setLocalSearch] = useState(initialSearch);
  const [localPagination, setLocalPagination] = useState(initialPagination);
  const [localColumnVisibility, setLocalColumnVisibility] = useState(initialColumnVisibility);

  const urlSync = useTableUrlSync({
    enabled,
    tableId,
    debounceMs,
    initialState: {
      sortModel: localSortModel,
      search: localSearch,
      pagination: localPagination,
      columnVisibility: localColumnVisibility
    },
    onStateChange: (urlState) => {
      // Update local state when URL changes
      if (urlState.sortModel !== undefined) {
        setLocalSortModel(urlState.sortModel);
      }
      if (urlState.search !== undefined) {
        setLocalSearch(urlState.search);
      }
      if (urlState.pagination) {
        setLocalPagination(urlState.pagination);
      }
      if (urlState.columnVisibility) {
        setLocalColumnVisibility(prev => ({
          ...prev,
          ...urlState.columnVisibility
        }));
      }
    }
  });

  // Current effective state (URL takes precedence)
  const currentState = urlSync.mergedState;

  // State setters that also update URL
  const setSortModel = useCallback((newSortModel) => {
    setLocalSortModel(newSortModel);
    urlSync.updateUrl({
      sortModel: newSortModel,
      search: currentState.search,
      pagination: currentState.pagination,
      columnVisibility: currentState.columnVisibility
    });
  }, [urlSync, currentState]);

  const setSearch = useCallback((newSearch) => {
    setLocalSearch(newSearch);
    urlSync.updateUrl({
      sortModel: currentState.sortModel,
      search: newSearch,
      pagination: { ...currentState.pagination, page: 0 }, // Reset to first page on search
      columnVisibility: currentState.columnVisibility
    });
  }, [urlSync, currentState]);

  const setPagination = useCallback((newPagination) => {
    setLocalPagination(newPagination);
    urlSync.updateUrl({
      sortModel: currentState.sortModel,
      search: currentState.search,
      pagination: newPagination,
      columnVisibility: currentState.columnVisibility
    });
  }, [urlSync, currentState]);

  const setColumnVisibility = useCallback((newColumnVisibility) => {
    setLocalColumnVisibility(newColumnVisibility);
    urlSync.updateUrl({
      sortModel: currentState.sortModel,
      search: currentState.search,
      pagination: currentState.pagination,
      columnVisibility: newColumnVisibility
    });
  }, [urlSync, currentState]);

  return {
    // Current state
    sortModel: currentState.sortModel,
    search: currentState.search,
    pagination: currentState.pagination,
    columnVisibility: currentState.columnVisibility,
    
    // State setters
    setSortModel,
    setSearch,
    setPagination,
    setColumnVisibility,
    
    // URL utilities
    clearUrlState: urlSync.clearUrlState,
    hasUrlState: urlSync.hasUrlState
  };
};
